// ─── renderResultEntry ───────────────────────────────────────────────────
// The body of a history card. Used by Step3History (the History wizard step in
// EscContentCreatorPro) and by RunDetailPage (the SA/TA run inspector, with its
// own light theme).
//
// Dispatch, in order:
//   cancelled entry (results.status 'cancelled' or results.cancelled)
//     → the «Annullato» notice
//   an entry the blog's builder draws (builderKeyFor === 'blog-seo')
//     → BlogSeoResultEntry, the blog's own screen (blogSeo/: the Testo | HTML |
//       META card)
//   an entry whose piece list is non-empty (publish/pieces :: piecesForEntry —
//   the ten known formats and the generic builder for a legacy object content)
//     → PiecesResultEntry, the ONE renderer for every other format
//   an entry with no pieces (a legacy string content, an unreadable one)
//     → the legacy branches below: a <pre> of the string, else the raw JSON
// There is no other renderer: the former per-format ones were deleted in
// phase 5 of plans/2026-10-05-1833-output-structure-proposal.md.
//
// rendererProps: { t, w, copiedId, onCopy } — t the theme object, w(alpha) the
// rgba helper, onCopy(text, id, { html }) from hooks/useClipboard.

import React from 'react';
import i18n from '../../i18n/index.js';
import PiecesResultEntry from './PiecesResultEntry.jsx';
import BlogSeoResultEntry, { isBlogSeoEntry } from './blogSeo/BlogSeoResultEntry.jsx';
import { piecesForEntry } from '../../publish/pieces/index.js';
import { FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

export default function renderResultEntry(entry, rendererProps) {
  const { t = {}, w, copiedId, onCopy } = rendererProps || {};
  const r = entry.results || {};
  const c = r.content;

  if (r.status === 'cancelled' || r.cancelled) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '16px 18px', borderRadius: RADII.control,
        background: SEMANTIC.attenzione.bg,
        border: `1px solid ${SEMANTIC.attenzione.bg}`,
        color: SEMANTIC.attenzione.fg, fontSize: '14px', fontWeight: 600,
      }}>
        <span>{i18n.t('outputs:renderEntry.cancelled')}</span>
      </div>
    );
  }

  if (piecesForEntry(entry).length > 0) {
    const Body = isBlogSeoEntry(entry) ? BlogSeoResultEntry : PiecesResultEntry;
    return (
      <Body
        entry={entry}
        copiedId={copiedId}
        onCopy={onCopy}
        t={t}
        w={w}
      />
    );
  }

  // Legacy: content is a markdown string
  if (c && typeof c === 'string') {
    return (
      <div>
        <pre style={{ fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: t.text, background: t.inputBg, border: `1px solid ${t.inputBorder}`, borderRadius: RADII.control, padding: '16px', margin: 0, fontFamily: FONT_SANS }}>{c}</pre>
      </div>
    );
  }

  // Fallback: raw JSON
  return <pre style={{ fontSize: '11px', overflowX: 'auto', color: t.text }}>{JSON.stringify(r, null, 2)}</pre>;
}
